import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostBinding,
    Inject,
    Injector,
    Input,
    OnChanges,
    Output,
    Renderer2,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { DropdownBaseWithFilter } from '@fi-shared/dropdown-base';
import { fiScrollBlockToElement, getObjectTypeNameTextId } from '@fi-shared/utils';
import { AppTextService } from '@fi-shared/app-text';
import { LocalSearchModel } from '@fi-shared/models';
import { NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors } from '@angular/forms';
import { DetectDevicesService } from '@fi-shared/fi-shared-services';

// todo not used?
@Component({
    selector: 'fi-objects-selector',
    templateUrl: './objects-selector.component.html',
    styleUrls: ['./objects-selector.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-objects-selector' },
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: ObjectsSelectorComponent,
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: ObjectsSelectorComponent,
            multi: true,
        },
    ],
    standalone: false,
})
export class ObjectsSelectorComponent extends DropdownBaseWithFilter implements OnChanges {
    //@Input() ngModel: { ObjectId: number; ObjectType }[];
    @Input() selectorSettings: LocalSearchModel;

    // deprecated , use ngModel ({ ObjectId: number; ObjectType }[])
    @Input() addedObjects: Array<any> = [];
    // deprecated , use ngModelChange
    @Output('addedObjectsChange') addedObjectsChange = new EventEmitter<any>();

    @Input() placeholder: string;
    @Input() disableTitle: string;
    @Input() displayField: string = 'Name';
    @Input() searchField: string = 'Name';
    @Input() multiSelect: boolean = true;
    @Input() disableAdd: boolean = false;

    // reject promise for cancel adding item
    @Input() onAddingItem: (item: { ObjectId: number; ObjectType }) => Promise<boolean> = () => Promise.resolve(true);
    // reject promise for cancel deleting item
    @Input() onDeletingItem: (item: { ObjectId: number; ObjectType }) => Promise<boolean> = () => Promise.resolve(true);

    @HostBinding('class.no-border')
    @Input()
    noBorder: boolean = false;

    @Input() required;

    @ViewChild('filterElement', { static: false }) filterElement: ElementRef;
    @ViewChild('resultsElement', { static: false }) resultsElement: ElementRef<HTMLElement>;
    SearchResults: Array<any> = [];
    filterText: string = '';
    keyboardIndex: number = -1;
    keyboardMaxIndex: number = -1;

    constructor(
        private injector: Injector,
        @Inject(Renderer2) renderer: Renderer2,
        @Inject(ElementRef) elementRef: ElementRef,
        protected appTextService: AppTextService,
        detectDevices: DetectDevicesService,
    ) {
        super(injector, renderer, elementRef, detectDevices);
    }

    ngOnChanges(changes: SimpleChanges): void {
        let change = changes.selectorSettings;
        if (change && change.firstChange) {
            this.updateSearchResultExecute();
        }

        change = changes.ngModel;
        if (change) {
            this.addedObjects = [];
            changes.ngModel.currentValue.forEach((item) => {
                this.selectorSettings.SearchData.forEach((sd) => {
                    const existingItem = sd.Data.find((searchDataItem) => {
                        return (
                            item.ObjectId === searchDataItem[this.selectorSettings.DataIdField] &&
                            item.ObjectType === searchDataItem[this.selectorSettings.DataObjectTypeField]
                        );
                    });
                    if (existingItem) {
                        this.addedObjects.push(existingItem);
                    }
                });
            });
            this.updateSearchResultExecute();
        }
    }

    onElementsAreaClick() {
        this.openDropdown();
    }

    getPlaceholder() {
        if (this.addedObjects.length === 0) {
            return this.placeholder;
        } else {
            return '';
        }
    }

    getTagTitle(object) {
        if (this.IsReadOnlyMode(object) && this.disableTitle) {
            return this.disableTitle;
        }

        return this.GetObjectTypeName(object);
    }

    IsReadOnlyMode(addedObject) {
        if (addedObject.IsEnabled === false) return true;

        return !this.selectorSettings.SearchData.some((items) => {
            return items.Data.some((item) => {
                return (
                    addedObject[this.selectorSettings.DataIdField] === item[this.selectorSettings.DataIdField] &&
                    addedObject[this.selectorSettings.DataObjectTypeField] ===
                        item[this.selectorSettings.DataObjectTypeField]
                );
            });
        });
    }

    GetObjectTypeName(object) {
        return this.appTextService.get(getObjectTypeNameTextId(object.ObjectType));
    }

    DeleteAdded(item) {
        this.onDeletingItem(item).then((result) => {
            if (!result) return;

            this.addedObjects.splice(this.addedObjects.indexOf(item), 1);
            this.doPropagateChanges();
            this.updateSearchResultExecute();
            this.cdr.markForCheck();
        });
    }

    SelectSearchItemClick($event: MouseEvent, searchItem) {
        $event.stopPropagation();
        this.onAddingItem(searchItem).then((result) => {
            if (!result) return;

            if (!this.multiSelect) {
                this.addedObjects = [];
            }

            this.addedObjects.push(searchItem);
            if (this.filterText) {
                setTimeout(() => (this.filterText = ''));
            }
            //this.SetFocusedInput();
            this.doPropagateChanges();
            this.updateSearchResultExecute();
            this.cdr.markForCheck();
        });
    }

    /*SetFocusedInput() {
    // commented because of https://chainformation.atlassian.net/browse/FI-2743
        this.filterElement.nativeElement.focus();
    }*/

    PopupDone() {
        this.closeDropdown();
    }

    closeDropdown() {
        super.closeDropdown();
    }

    doPropagateChanges() {
        this.addedObjectsChange.emit(this.addedObjects);
        this.ngModel = this.addedObjects.map((item) => ({
            ObjectId: item[this.selectorSettings.DataIdField],
            ObjectType: item[this.selectorSettings.DataObjectTypeField],
        }));
        super.doPropagateChanges();
    }

    getObjectSearchKey(item) {
        return item[this.selectorSettings.DataIdField] + ':' + item[this.selectorSettings.DataObjectTypeField];
    }

    updateSearchResult() {
        setTimeout(() => this.updateSearchResultExecute());
    }

    updateSearchResultExecute() {
        const existingObjectsKeys = this.getObjectsArrayKeys(this.addedObjects);
        this.SearchResults = [];
        this.selectorSettings.SearchData.forEach((group) => {
            let groupData = [];
            group.Data.forEach((dataItem) => {
                if (existingObjectsKeys.indexOf(this.getObjectSearchKey(dataItem)) !== -1) {
                    return;
                }

                if (
                    this.filterText.length > 0 &&
                    dataItem[this.searchField].toLowerCase().indexOf(this.filterText.toLowerCase()) === -1
                ) {
                    return;
                }

                groupData.push(dataItem);
            });

            if (groupData.length > 0) {
                this.SearchResults.push(Object.assign({}, group, { Data: groupData }));
            }
        });

        this.keyboardMaxIndex = this.selectorSettings.SearchData.reduce((sum, item) => sum + item.Data.length, 0) - 1;
    }

    getObjectsArrayKeys(arr: Array<any>) {
        return arr.map((item) => this.getObjectSearchKey(item));
    }

    openDropdown(forceOpen?: boolean) {
        if (this.disableAdd && !forceOpen) return;

        super.openDropdown();
        this.keyboardIndex = -1;
        this.updateSearchResult();
    }

    filterChange() {
        super.filterChange();
        this.keyboardIndex = -1;
        this.updateKeyboardSelectedIndex();
        this.updateSearchResultExecute();
    }

    getResultsBlocks() {
        return this.elementRef.nativeElement.querySelectorAll('.CcmShareSearchResultsItem');
    }

    getKeyboardSelectedItem() {
        return this.elementRef.nativeElement.querySelector(
            '.CcmShareSearchResultsItem.BrandedBackgroundColorLowOpacity',
        );
    }

    updateKeyboardSelectedIndex() {
        const current = this.getKeyboardSelectedItem();
        if (current) {
            this.renderer.removeClass(current, 'BrandedBackgroundColorLowOpacity');
        }
        if (this.keyboardIndex > -1) {
            this.renderer.addClass(this.getResultsBlocks()[this.keyboardIndex], 'BrandedBackgroundColorLowOpacity');
        }
    }

    keyboardIndexChange() {
        this.updateKeyboardSelectedIndex();
        fiScrollBlockToElement(this.resultsElement.nativeElement, this.getKeyboardSelectedItem());
    }

    keyboardIndexSelected() {
        const current = this.getKeyboardSelectedItem();
        if (current) {
            current.click();
        }
        this.keyboardIndex = -1;
    }

    doValidate(): ValidationErrors {
        return {
            required: this.required && (!this.ngModel || !this.ngModel.length),
        };
    }
}
